import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  FaCalendarAlt,
  FaCarSide,
  FaCheckCircle,
  FaClock,
  FaFilter,
  FaMapSigns,
  FaPlayCircle,
  FaRedo,
  FaRoad,
  FaTimesCircle,
  FaUserCheck,
  FaUserTimes,
} from "react-icons/fa";
import TripsMetricsService from "../services/TripsMetricsService";

const initialMetrics = {
  requested: 0,
  accepted: 0,
  declined: 0,
  cancelledByDriver: 0,
  cancelledByUser: 0,
  arrived: 0,
  started: 0,
  completed: 0,
};

const DashboardTripsMetrics = () => {
  const [metrics, setMetrics] = useState(initialMetrics);
  const [loading, setLoading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    tripType: "Both",
    scheduleType: "Both",
  });

  const fetchMetrics = async (params = {}) => {
    setLoading(true);
    try {
      const response = await TripsMetricsService.getTripsMetrics(params);
      if (response?.success) {
        setMetrics(response?.data?.metrics || initialMetrics);
        setFiltersApplied(Boolean(response?.data?.filtersApplied));
      } else {
        toast.error("Failed to load trips metrics");
      }
    } catch (error) {
      console.error("Trips metrics fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const handleApply = () => {
    const { startDate, endDate, tripType, scheduleType } = filters;
    const hasAnyDate = Boolean(startDate || endDate);
    const hasBothDates = Boolean(startDate && endDate);

    if (hasAnyDate && !hasBothDates) {
      toast.warning("Please select both start and end dates");
      return;
    }

    if (hasBothDates && startDate > endDate) {
      toast.warning("Start date cannot be greater than end date");
      return;
    }

    const params = {};
    if (hasBothDates) {
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (tripType && tripType !== "Both") {
      params.tripType = tripType;
    }
    if (scheduleType && scheduleType !== "Both") {
      params.scheduleType = scheduleType;
    }

    fetchMetrics(params);
  };

  const handleClear = () => {
    setFilters({
      startDate: "",
      endDate: "",
      tripType: "Both",
      scheduleType: "Both",
    });
    fetchMetrics();
  };

  const cards = [
    {
      key: "requested",
      label: "Requested",
      value: metrics.requested,
      icon: <FaRoad className="text-2xl" />,
      style: "from-blue-500 to-blue-600",
    },
    {
      key: "accepted",
      label: "Accepted",
      value: metrics.accepted,
      icon: <FaUserCheck className="text-2xl" />,
      style: "from-green-500 to-green-600",
    },
    // {
    //   key: "declined",
    //   label: "Declined",
    //   value: metrics.declined,
    //   icon: <FaTimesCircle className="text-2xl" />,
    //   style: "from-purple-500 to-purple-600",
    // },
    {
      key: "cancelledByDriver",
      label: "Cancelled by Driver",
      value: metrics.cancelledByDriver,
      icon: <FaCarSide className="text-2xl" />,
      style: "from-orange-500 to-orange-600",
    },
    {
      key: "cancelledByUser",
      label: "Cancelled by User",
      value: metrics.cancelledByUser,
      icon: <FaUserTimes className="text-2xl" />,
      style: "from-rose-500 to-rose-600",
    },
    {
      key: "arrived",
      label: "Arrived",
      value: metrics.arrived,
      icon: <FaMapSigns className="text-2xl" />,
      style: "from-cyan-500 to-cyan-600",
    },
    {
      key: "started",
      label: "Started",
      value: metrics.started,
      icon: <FaPlayCircle className="text-2xl" />,
      style: "from-amber-500 to-amber-600",
    },
    {
      key: "completed",
      label: "Completed",
      value: metrics.completed,
      icon: <FaCheckCircle className="text-2xl" />,
      style: "from-emerald-500 to-emerald-600",
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
      <div className="mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">
          Trips Metrics
        </h3>
        <p className="text-gray-600 text-sm">
          {filtersApplied
            ? "Filtered by selected created date range"
            : "Overall totals across all rides"}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Start Date
          </label>
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, startDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            End Date
          </label>
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, endDate: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trip Type
          </label>
          <select
            value={filters.tripType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, tripType: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="Both">Both</option>
            <option value="One-Way">One-Way</option>
            <option value="Round-Trip">Round-Trip</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Immediate / Scheduled
          </label>
          <select
            value={filters.scheduleType}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, scheduleType: e.target.value }))
            }
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="Both">Both</option>
            <option value="Now">Immediate</option>
            <option value="Scheduled">Scheduled</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleApply}
            disabled={loading}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-60"
          >
            <FaFilter className="inline mr-2" />
            Filter Apply
          </button>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleClear}
            disabled={loading}
            className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-60"
          >
            <FaRedo className="inline mr-2" />
            Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-600">
          <FaCalendarAlt className="inline mr-2 animate-pulse" />
          Loading trips metrics...
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cards.map((card) => (
            <div
              key={card.key}
              className={`bg-gradient-to-br ${card.style} rounded-xl shadow-lg p-6 text-white`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white/90">{card.label}</p>
                  <p className="text-3xl font-bold mt-1">
                    {Number(card.value || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-3">{card.icon}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardTripsMetrics;
